/**
 * AIPDashboard Component
 *
 * This component represents the AIP Dashboard.
 * It displays an iframe that loads the AIP website after authentication.
 * The component also handles the postPageVisited action for web analytics.
 *
 * Example usage:
 *
 * <AIPDashboard />
 */

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from '../../../components/Loader/Loader';
import {
  IVisitedPagePayload,
  SectionType,
  TrafficAnalyticsActions,
} from '../../../libs/store/actions/WebAnalytics/TrafficAnalyticsActions';
import { useAppSelector, IApplicationState } from '../../../libs/store/reducers';
import { getAIPMSALConfig } from '../../CBOLogin/CBOLoginConstants';
import { STORAGE_CONSTANTS, storage } from '../../../libs/services/LocalStorage';
import { formatUrl, handleZoom, removeCookie } from '../../../libs/helper/HelperFunctions';
import * as msal from '@azure/msal-browser';
import { CBOKeyAssets } from '../../CBOSidebar/CBOSidebarConstants';
import { IProduct } from '../../../libs/models/cbo/EngagementModels';

interface IAIPDashboardProps {
  selectedClientName?: string;
  selectedAssetConfig?: IProduct;
}
const AIPDashboard = ({ selectedClientName, selectedAssetConfig }: IAIPDashboardProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState<boolean>();
  const dispatcher = {
    /**
     * Dispatches the postVisitedPageStart action to track the visited page for web analytics.
     *
     * @param data - The payload containing the page information.
     */
    postPageVisited: (data: IVisitedPagePayload) =>
      dispatch(TrafficAnalyticsActions.postVisitedPageStart(data)),
  };
  const {
    selectedEnagagementName,
    selectedEnagagementId,
    isEngagement,
    selectedClientId,
    keyAssetlistDefault,
  } = useAppSelector((state: IApplicationState) => ({
    selectedEnagagementName: state.cboViewSummary.selectedEnagagementName,
    selectedEnagagementId: state.cboViewSummary.selectedEnagagementId,
    isEngagement: state.cboViewSummary.isEngagement,
    selectedClientId: state.cboViewSummary.selectedClientId,
    keyAssetlistDefault: state.engagement.KeyAssetDefaultList,
  }));

  const authenticateAIP = async () => {
    const msalInstance = new msal.PublicClientApplication(getAIPMSALConfig());
    storage?.removeSessionItem(STORAGE_CONSTANTS.msalInteractionStatus);
    removeCookie(STORAGE_CONSTANTS.msalInteractionStatus);
    await msalInstance.initialize();
    msalInstance.loginRedirect();
  };

  useEffect(() => {
    // Track the visited page for web analytics
    dispatcher.postPageVisited({
      sectionType: isEngagement ? SectionType.ENG_ASSET : SectionType.PURSUIT_ASSET,
      assetName: SectionType.AIP,
      name: selectedEnagagementName,
      id: selectedEnagagementId,
      clientId: selectedClientId,
      clientName: selectedClientName,
    });
    const aipAuthenticated: any = isEngagement
      ? storage.getItemSession(STORAGE_CONSTANTS.aipEngAuthenticated)
      : storage.getItemSession(STORAGE_CONSTANTS.aipAuthenticated);
    if (aipAuthenticated === 'true') {
      console.log('AIP Dashboard- AIP authenticated');
      setAuthenticated(true);
    } else {
      console.log('AIP not authenticated');
      setAuthenticated(false);
      storage.setItem(STORAGE_CONSTANTS.authOriginatorUrl, window.location.pathname);
      authenticateAIP();
    }
    return () => {
      storage?.removeSessionItem(STORAGE_CONSTANTS.aipAuthenticated);
      // Track the visited page for web analytics when the component is unmounted
      dispatcher.postPageVisited({
        sectionType: isEngagement ? SectionType.ENG_ASSET : SectionType.PURSUIT_ASSET,
        assetName: SectionType.AIP,
        name: selectedEnagagementName,
        id: selectedEnagagementId,
        clientId: selectedClientId,
        clientName: selectedClientName,
      });
    };
  }, []);
  const selectedAssetConfigDefault = keyAssetlistDefault?.find(
    (keyAsset) => keyAsset.product?.toLowerCase() === selectedAssetConfig?.product.toLowerCase(),
  );
  const getIframeUrl = (instanceUrl?: string) => {
    return `${formatUrl(instanceUrl ?? '')}/v2/generate-output?ascend_project_id=${isEngagement ? selectedEnagagementId : selectedAssetConfigDefault?.projectId}`;
  };
  return (
    <div className="trunorth-dashboard-wrapper">
      {authenticated && (
        <Loader loaderName="Authorizing..." loading={isLoading}>
          <iframe
            id="assess"
            className="assess-iframe"
            title="Assess"
            src={getIframeUrl(selectedAssetConfigDefault?.instanceUrl)}
            onLoad={() => {
              setIsLoading(false);
              handleZoom('assess-iframe');
            }}
          />
        </Loader>
      )}
    </div>
  );
};

export default React.memo(AIPDashboard);
